import { useEffect, type RefObject } from 'react';

/** Close an open menu when attention leaves it.
 *
 * A menu that stays open after you have looked away from it reads as stuck.
 * Both halves matter and both are easy to forget: a pointer press outside the
 * group, and Escape. Shared rather than copied because the bug it prevents is
 * invisible until somebody meets it.
 */
export default function useDismiss(
  open: boolean,
  group: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!group.current?.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, group, onClose]);
}
